import { useCallback, useMemo } from 'react';
import { apiCreateSlideSelect, apiDeleteSlideSelect, apiDeleteSlideSelectAll, } from '../../../../actions/ProffitSearchSelectAction';
import { PROFFIT_SEARCH_SEARCH_UNIT_SLIDE } from '../../../../define/consts/proffit_search/search';
import { wrapDispatch } from '../../../action';
import { slideSearchPageHelper } from '../../../page/SlideSearch';
/**
 * スライドページの選択状態を制御するuse
 * @param list
 * @param profile
 * @param proffitSearch
 * @param unit
 * @param dispatch
 * @returns {*}
 */
const useSlideSearchCommonSelectPage = (list, profile, proffitSearch, unit, dispatch) => {
    const { enableDomains: rawEnableDomains } = profile;
    /**
     * APIのリクエストなどで使用する値を返す
     * @type {function(*=): {requestKey: string, fileType: string}}
     */
    const getApiMetaValues = useCallback((unitValue) => {
        const isPage = unitValue === PROFFIT_SEARCH_SEARCH_UNIT_SLIDE.value;
        return {
            fileType: isPage ? 'page' : 'slide',
            isPage,
            requestKey: slideSearchPageHelper.getRequestKeyFromUnitValue(unitValue),
        };
    }, []);
    const { enableDomains, isAllListSelected, searchList, selectMap, selectSlide, } = useMemo(() => {
        // サーバーからの選択状態
        const rawSelectSlide = proffitSearch.selectSlide || [];
        // 選択中の値をidの配列に変換
        const memoSelectList = rawSelectSlide
            .map((v) => v.slideSearchSlideUuid || v.slideSearchSlidePageUuid)
            .filter((v) => v);
        const { isPage, requestKey } = getApiMetaValues(unit.value);
        // 検索結果リストを整形
        const memoSearchList = list.map((v) => {
            // ファイル単位で表示中の場合はそのまま返す
            if (!isPage) {
                return v.slide;
            }
            // スライド単位で表示中の場合、pagesから該当のpageを取得する
            const { pageId, slide: { pages }, } = v;
            return pages.find((page) => page[requestKey] === pageId);
        });
        // 検索結果リストが全て選択状態か否か
        const memoIsAllListSelected = memoSearchList.length > 0 &&
            memoSearchList.every((v) => memoSelectList.includes(v[requestKey]));
        // mapの状態
        const memoSelectMap = {};
        memoSelectList.forEach((v) => {
            memoSelectMap[v] = true;
        });
        return {
            enableDomains: rawEnableDomains || [],
            isAllListSelected: memoIsAllListSelected,
            searchList: memoSearchList,
            selectMap: memoSelectMap,
            selectSlide: rawSelectSlide,
        };
    }, [getApiMetaValues, list, proffitSearch, rawEnableDomains, unit]);
    /**
     * チェックボックス切り替え時のイベント（値の直接設定用）
     * @type {function(*, *=, *=, *): Promise<unknown>}
     */
    const onChangeSelectSlideRaw = useCallback((id, value, unitValue, checked) => {
        const { fileType, requestKey } = getApiMetaValues(unitValue);
        const idList = [id];
        const action = checked
            ? apiCreateSlideSelect(fileType, { [requestKey]: idList }, { key: unitValue, [requestKey]: [value] })
            : apiDeleteSlideSelect(fileType, requestKey, idList, {
                idList,
                key: unitValue,
            });
        return wrapDispatch(dispatch, action);
    }, [dispatch, getApiMetaValues]);
    /**
     * チェックボックス切り替え時のイベント
     * @type {function(*, *): Promise<unknown>}
     */
    const onChangeSelectSlide = useCallback((e, model) => {
        const { slide } = model;
        const { checked, dataset, value } = e.currentTarget;
        const { unit: unitValue } = dataset;
        const targetModel = unitValue === PROFFIT_SEARCH_SEARCH_UNIT_SLIDE.value
            ? slide.pages.find((v) => v.id === value)
            : slide;
        return onChangeSelectSlideRaw(value, targetModel, unitValue, checked);
    }, [onChangeSelectSlideRaw]);
    /**
     * チェックボックス切り替え時のイベント（一括）
     * @type {(function(*): (null|Promise<unknown>))|*}
     */
    const onChangeSelectSlideAll = useCallback((e) => {
        const { checked, dataset } = e.currentTarget;
        const { unit: unitValue } = dataset;
        const { fileType, requestKey } = getApiMetaValues(unitValue);
        // APIに投げるuuidの配列
        const idList = searchList.map((v) => v[requestKey]).filter((v) => v);
        // 選択データが存在しない場合は何もしない
        if (!idList.length) {
            e.preventDefault();
            return null;
        }
        const action = checked
            ? apiCreateSlideSelect(fileType, { [requestKey]: idList }, { key: unitValue, [requestKey]: searchList })
            : apiDeleteSlideSelect(fileType, requestKey, idList, {
                idList,
                key: unitValue,
            });
        return wrapDispatch(dispatch, action);
    }, [dispatch, getApiMetaValues, searchList]);
    /**
     * チェック状態を全件削除する関数
     * @type {(function(): void)|*}
     */
    const allClear = useCallback(() => {
        dispatch(apiDeleteSlideSelectAll());
    }, [dispatch]);
    return {
        allClear,
        enableDomains,
        isAllListSelected,
        onChangeSelectSlide,
        onChangeSelectSlideAll,
        onChangeSelectSlideRaw,
        selectMap,
        selectSlide,
    };
};
/**
 * スライドページのアクションの可否を制御するuse
 * @param selectSlides
 * @returns {{isBlockDownload, isBlockShare, isNotReadyPptx}}
 */
const useSlideSearchCommonAllowAction = (selectSlides) => {
    const { isBlockDownload, isBlockShare, isNotReadyPptx } = useMemo(() => ({
        isBlockDownload: !!selectSlides.find((v) => v.blockDownload),
        isBlockShare: !!selectSlides.find((v) => v.blockShare),
        isNotReadyPptx: !selectSlides.every((v) => v.canDownload),
    }), [selectSlides]);
    return {
        isBlockDownload,
        isBlockShare,
        isNotReadyPptx,
    };
};
export const useSlideSearchCommonSelect = {
    useSlideSearchCommonAllowAction,
    useSlideSearchCommonSelectPage,
};
export default useSlideSearchCommonSelect;
